import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Page, StatusBadge } from "@/components/Shell";
import { StepRail } from "@/components/TaskCards";
import { api, errMsg, fmtTime } from "@/lib/api";

export default function Tasks() {
  const [items, setItems] = useState(null);
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/tasks").then((r) => setItems(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);
  const active = (items || []).some((t) => ["queued", "running"].includes(t.status));
  useEffect(() => { if (!active) return; const h = setInterval(load, 3000); return () => clearInterval(h); }, [active]);

  const start = async (e) => {
    e.preventDefault();
    if (query.trim().length < 3) return;
    setBusy(true);
    try { await api.post("/tasks", { query }); setQuery(""); load(); toast("Research task queued."); }
    catch (err) { toast.error(errMsg(err)); }
    finally { setBusy(false); }
  };

  return (
    <Page eyebrow="Background work" title="Tasks" intro="Research that continues after you close the page. Each task keeps its steps, logs, retries and sources.">
      <form onSubmit={start} className="flex flex-col sm:flex-row gap-4 mb-16 max-w-3xl" data-testid="new-task-form">
        <input value={query} onChange={(e) => setQuery(e.target.value)} data-testid="new-task-input" placeholder="Research a question directly…"
          className="flex-1 bg-transparent border-b border-[#2A2A2A] focus:border-[#D8BFD8] outline-none py-3 text-ivory placeholder:text-[#4a4a4a] transition-colors" />
        <button disabled={busy} data-testid="new-task-submit" className="px-6 py-3 rounded-full border border-[#2A2A2A] text-[#C0C0C0] text-xs tracking-[0.2em] uppercase hover:border-[#C0C0C0]/60 hover:text-ivory transition-colors disabled:opacity-40">Start research</button>
      </form>
      {items && items.length === 0 && <p className="font-display italic text-2xl text-[#5f5f5f]" data-testid="tasks-empty">No tasks have run yet.</p>}
      <ol className="relative border-l border-[#1c1c1c] ml-2" data-testid="task-timeline">
        {(items || []).map((t) => (
          <li key={t.id} className="relative pl-8 md:pl-12 pb-12">
            <span className={`absolute -left-[5px] top-2 h-2.5 w-2.5 rounded-full ${t.status === "running" ? "bg-[#D8BFD8] animate-pulse" : t.status === "completed" ? "bg-[#C0C0C0]" : t.status === "failed" ? "bg-[#e2a6a6]" : "bg-[#3a3a3a]"}`} />
            <p className="text-xs text-[#5f5f5f] tracking-wide">{fmtTime(t.created_at)}</p>
            <div className="mt-2 flex flex-wrap items-start gap-4 justify-between max-w-3xl">
              <Link to={`/tasks/${t.id}`} data-testid={`task-link-${t.id}`} className="font-display text-2xl text-ivory hover:text-[#D8BFD8] transition-colors max-w-xl">{t.title}</Link>
              <StatusBadge status={t.status} testId={`task-status-${t.id}`} />
            </div>
            <div className="max-w-3xl"><StepRail steps={t.steps} /></div>
            {t.error && <p className="mt-3 text-sm text-[#e2a6a6] max-w-3xl">{t.error}</p>}
          </li>
        ))}
      </ol>
    </Page>
  );
}
